import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { TimeClock } from '@mui/x-date-pickers/TimeClock';
import moment, { Moment } from 'moment';
import { useStyles } from './HourRangePickerStyles';
import { useLocale } from '../../i18n/LocaleContext';
import type { MessageKey } from '../../i18n/translations';

export type DayMark = 'off' | 'holiday';

export type HourRange = {
  startMinutes: number | null;
  endMinutes: number | null;
  mark?: DayMark;
};

export const EMPTY_RANGE: HourRange = { startMinutes: null, endMinutes: null };

type ClockView = 'hours' | 'minutes';

type HourRangePickerProps = {
  label: string;
  value: HourRange;
  onChange: (next: HourRange) => void;
  selectionMode?: boolean;
  selected?: boolean;
  onSelect?: () => void;
};

const minutesToMoment = (minutes: number | null): Moment | null =>
  minutes === null ? null : moment().startOf('day').add(minutes, 'minutes');

const momentToMinutes = (value: Moment | null): number | null =>
  value && value.isValid() ? value.hours() * 60 + value.minutes() : null;

export const formatTime = (minutes: number) =>
  moment().startOf('day').add(minutes, 'minutes').format('HH:mm');

export const formatDurationFromMinutes = (totalMinutes: number) => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (totalMinutes === 0) return '0h';
  if (minutes === 0) return `${hours}h`;
  if (hours === 0) return `${minutes}m`;
  return `${hours}h ${minutes}m`;
};

const formatDuration = (startMinutes: number, endMinutes: number) =>
  formatDurationFromMinutes(endMinutes - startMinutes);

export const getShiftMinutes = (range: HourRange) => {
  if (range.mark || range.startMinutes === null || range.endMinutes === null) {
    return 0;
  }
  return range.endMinutes - range.startMinutes;
};

export const hasDayEntry = (range: HourRange) =>
  Boolean(range.mark) ||
  (range.startMinutes !== null && range.endMinutes !== null);

export const cloneRange = (range: HourRange): HourRange => ({
  startMinutes: range.startMinutes,
  endMinutes: range.endMinutes,
  mark: range.mark,
});

export const formatShiftSummary = (
  range: HourRange,
  t: (key: MessageKey) => string,
) => {
  if (range.mark === 'off') return t('off');
  if (range.mark === 'holiday') return t('holiday');
  if (range.startMinutes !== null && range.endMinutes !== null) {
    return `${formatTime(range.startMinutes)} – ${formatTime(range.endMinutes)} (${formatDuration(range.startMinutes, range.endMinutes)})`;
  }
  return t('empty');
};

const HourRangePicker = ({
  label,
  value,
  onChange,
  selectionMode = false,
  selected = false,
  onSelect,
}: HourRangePickerProps) => {
  const classes = useStyles();
  const { t } = useLocale();
  const isPhone = useMediaQuery('(max-width: 480px)');
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<'start' | 'end'>('start');
  const [view, setView] = useState<ClockView>('hours');
  const [clockValue, setClockValue] = useState<Moment | null>(null);
  const [draftStart, setDraftStart] = useState<number | null>(null);

  const hasShift = value.startMinutes !== null && value.endMinutes !== null;
  const hasEntry = hasDayEntry(value);

  const resetClock = () => {
    setView('hours');
    setClockValue(null);
  };

  const openDialog = () => {
    setStep('start');
    setDraftStart(null);
    resetClock();
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
    setStep('start');
    setDraftStart(null);
    resetClock();
  };

  const handleTimeChange = (
    nextValue: Moment | null,
    selectionState?: 'partial' | 'shallow' | 'finish',
  ) => {
    setClockValue(nextValue);
    if (selectionState !== 'finish') return;

    const minutes = momentToMinutes(nextValue);
    if (minutes === null) return;

    if (step === 'start') {
      setDraftStart(minutes);
      setStep('end');
      resetClock();
      return;
    }

    onChange({ startMinutes: draftStart, endMinutes: minutes });
    closeDialog();
  };

  const handleMark = (mark: DayMark) => {
    onChange({ startMinutes: null, endMinutes: null, mark });
    closeDialog();
  };

  const handleClear = () => {
    onChange(EMPTY_RANGE);
    closeDialog();
  };

  const triggerClass = [
    classes.trigger,
    value.mark === 'off' ? classes.offTrigger : '',
    value.mark === 'holiday' ? classes.holidayTrigger : '',
    selectionMode ? classes.selectionTrigger : '',
    selected ? classes.selectedTrigger : '',
  ]
    .filter(Boolean)
    .join(' ');

  const hoursLabel = clockValue ? clockValue.format('HH') : '--';
  const minutesLabel = clockValue ? clockValue.format('mm') : '--';
  const previewEndMinutes = momentToMinutes(clockValue);
  const previewDuration =
    step === 'end' && draftStart !== null && previewEndMinutes !== null
      ? formatDuration(draftStart, previewEndMinutes)
      : null;

  return (
    <>
      <div className={classes.cellWrap}>
        <button
          type="button"
          className={triggerClass}
          onClick={selectionMode ? onSelect : openDialog}
        >
          {value.mark === 'off' && (
            <span className={classes.offLabel}>{t('off')}</span>
          )}
          {value.mark === 'holiday' && (
            <span className={classes.holidayLabel}>{t('holiday')}</span>
          )}
          {hasShift && !value.mark && (
            <>
              <span>
                {formatTime(value.startMinutes as number)} –{' '}
                {formatTime(value.endMinutes as number)}
              </span>
              <span className={classes.duration}>
                {formatDuration(
                  value.startMinutes as number,
                  value.endMinutes as number,
                )}
              </span>
            </>
          )}
          {!hasEntry && (
            <span className={classes.placeholder}>{t('setHours')}</span>
          )}
        </button>
        {hasEntry && !selectionMode && (
          <button
            type="button"
            className={classes.deleteButton}
            aria-label={t('deleteAria', { label })}
            onClick={handleClear}
          >
            ×
          </button>
        )}
      </div>

      <Dialog
        open={open}
        onClose={closeDialog}
        maxWidth="xs"
        fullWidth
        fullScreen={isPhone}
      >
        <DialogTitle
          sx={
            isPhone
              ? { pt: 'max(16px, env(safe-area-inset-top))' }
              : undefined
          }
        >
          {label}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <div className={classes.dayMarks}>
            <Button
              variant={value.mark === 'off' ? 'contained' : 'outlined'}
              color="inherit"
              onClick={() => handleMark('off')}
            >
              {t('off')}
            </Button>
            <Button
              variant={value.mark === 'holiday' ? 'contained' : 'outlined'}
              color="warning"
              onClick={() => handleMark('holiday')}
            >
              {t('holiday')}
            </Button>
          </div>
          <Typography variant="subtitle1">
            {step === 'start' ? t('pickStart') : t('pickEnd')}
          </Typography>
          {step === 'end' && draftStart !== null && (
            <Typography variant="body2" color="text.secondary">
              {t('fromTime', { time: formatTime(draftStart) })}
              {previewDuration ? ` · ${previewDuration}` : ''}
            </Typography>
          )}
          <div className={classes.timeDisplay}>
            <button
              type="button"
              className={`${classes.timeUnit} ${view === 'hours' ? classes.activeUnit : ''}`}
              onClick={() => setView('hours')}
            >
              {hoursLabel}
            </button>
            <span className={classes.timeSeparator}>:</span>
            <button
              type="button"
              className={`${classes.timeUnit} ${view === 'minutes' ? classes.activeUnit : ''}`}
              onClick={() => setView('minutes')}
            >
              {minutesLabel}
            </button>
          </div>
          <div className={classes.clockWrap}>
            <TimeClock
              key={step}
              ampm={false}
              autoFocus
              views={['hours', 'minutes']}
              view={view}
              onViewChange={(nextView) => setView(nextView as ClockView)}
              minutesStep={30}
              value={clockValue}
              referenceDate={moment().startOf('day')}
              shouldDisableTime={(timeValue, clockView) => {
                if (clockView === 'minutes' && timeValue.minutes() % 30 !== 0) {
                  return true;
                }

                if (step !== 'end' || draftStart === null) return false;

                if (clockView === 'hours') {
                  return timeValue.hours() * 60 + 30 <= draftStart;
                }

                return timeValue.hours() * 60 + timeValue.minutes() <= draftStart;
              }}
              onChange={handleTimeChange}
            />
          </div>
        </DialogContent>
        <DialogActions
          sx={
            isPhone
              ? { pb: 'max(8px, env(safe-area-inset-bottom))' }
              : undefined
          }
        >
          {step === 'end' && (
            <Button
              onClick={() => {
                setStep('start');
                setDraftStart(null);
                resetClock();
              }}
            >
              {t('back')}
            </Button>
          )}
          {hasEntry && (
            <Button color="error" onClick={handleClear}>
              {t('delete')}
            </Button>
          )}
          <Button onClick={closeDialog}>{t('cancel')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HourRangePicker;
